// apps/web/src/features/profile/components/ProfilePage.tsx
//
// User-scoped /profile page. Three sections:
//   1. View — read-only snapshot of own User row (name, email, phone, role,
//      department, createdAt) sourced from the auth store.
//   2. Edit — RHF form for the only two fields the wire schema permits to
//      self-patch: name + phone. The form holds both fields populated; the
//      submit handler sends only the diff via PATCH /users/me. On success
//      it swaps the user in the store via setSession (preserving access
//      token + permissions) so the topbar plate updates immediately.
//   3. Change password — opens the existing ChangePasswordSelfModal (which
//      logs the user out on success — that flow lives in the modal itself).
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { phoneSchema, type SelfPatchUserInput, type User } from '@magimp/shared';
import { usePatchSelf } from '@magimp/shared-hooks';
import { Button, Input, PhoneInput, FormRow } from '../../../ui/index.js';
import { ChangePasswordSelfModal } from '../../../account/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { formatDateTime } from '../../../i18n/index.js';
import { phoneError, pickChangedFields } from '../../../shared/index.js';

// WHY: keep this form schema separate from `SelfPatchUserInputSchema` (which
// is partial + at-least-one). The form ALWAYS holds both fields, so each one
// must individually validate; the diffing happens at submit time.
//
// Field-level error messages are injected at schema-build time via `t()` so
// ru/zh-Hans users never see raw zod copy. The phone validator's
// `'invalid E.164 phone'` message is translated at the FormRow level via
// `phoneError()` because it lives inside `phoneSchema` from `@magimp/shared`
// and is shared with backend validation; localizing it inline would require
// reaching into the package's schema and is not part of this issue.
const createProfileFormSchema = (messages: { nameRequired: string; nameTooLong: string }) =>
  z.object({
    name: z.string().trim().min(1, messages.nameRequired).max(200, messages.nameTooLong),
    phone: phoneSchema,
  });
type ProfileFormValues = z.infer<ReturnType<typeof createProfileFormSchema>>;

const ROW_KEYS = ['name', 'phone'] as const satisfies ReadonlyArray<keyof ProfileFormValues>;

export function ProfilePage() {
  const { t } = useTranslation('profile');
  const user = useAuthStore((s) => s.user);
  const showToast = useToastStore((s) => s.show);
  const patch = usePatchSelf();
  const [pwOpen, setPwOpen] = useState(false);

  const schema = useMemo(
    () =>
      createProfileFormSchema({
        nameRequired: t('errors.nameRequired'),
        nameTooLong: t('errors.nameTooLong'),
      }),
    [t],
  );
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: user?.name ?? '', phone: user?.phone ?? '' },
  });

  if (!user) return null;

  const submitting = isSubmitting || patch.isPending;
  const departmentLabel = user.department?.name ?? user.departmentId ?? t('placeholders.missing');

  const onSubmit = (values: ProfileFormValues) => {
    const baseline: ProfileFormValues = { name: user.name, phone: user.phone };
    const diff = pickChangedFields(
      values as unknown as Record<string, unknown>,
      baseline as unknown as Record<string, unknown>,
      ROW_KEYS as unknown as (keyof Record<string, unknown>)[],
    ) as SelfPatchUserInput;
    if (Object.keys(diff).length === 0) return;
    patch.mutate(diff, {
      onSuccess: (updated: User) => {
        const { accessToken, permissions, isOnShift } = useAuthStore.getState();
        const nextUser: User =
          updated.department === undefined && user.department !== undefined
            ? { ...updated, department: user.department }
            : updated;
        // INVARIANT: setSession requires a non-null accessToken; if the
        // session evaporated mid-request, leave the store alone — the next
        // refresh tick will reconcile.
        // WHY (#440 Phase 3): preserve the existing top-level `isOnShift`
        // slot through self-edit. The PATCH /api/users/me response is a
        // bare User (no shift state — `UserSchema` no longer carries it,
        // and admin shift writes go through /api/users/me/shift only), so
        // setSession must reuse the captured slot value instead of trying
        // to source it from `updated`.
        if (accessToken)
          useAuthStore
            .getState()
            .setSession(accessToken, nextUser, permissions, isOnShift ?? false);
        reset({ name: nextUser.name, phone: nextUser.phone });
        showToast({ tone: 'success', text: t('toasts.saved') });
      },
      onError: (err) => {
        showToast({ tone: 'error', text: errorMessage(err) });
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{t('title')}</h1>
      </header>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{t('sections.view')}</h2>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 24,
            rowGap: 6,
            margin: 0,
          }}
        >
          <dt style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('fields.name')}</dt>
          <dd style={{ margin: 0 }}>{user.name}</dd>
          <dt style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('fields.email')}</dt>
          <dd style={{ margin: 0 }}>{user.email}</dd>
          <dt style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('fields.phone')}</dt>
          <dd style={{ margin: 0 }}>{user.phone}</dd>
          <dt style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('fields.role')}</dt>
          <dd style={{ margin: 0 }}>{user.role.displayName}</dd>
          <dt style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('fields.department')}</dt>
          <dd style={{ margin: 0 }}>{departmentLabel}</dd>
          <dt style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('fields.createdAt')}</dt>
          <dd style={{ margin: 0 }}>{formatDateTime(user.createdAt)}</dd>
        </dl>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 480 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{t('sections.edit')}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(onSubmit)(e);
          }}
          noValidate
        >
          <FormRow label={t('fields.name')} error={errors.name?.message}>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <Input
                  value={field.value ?? ''}
                  onChange={(v) => field.onChange(v)}
                  invalid={!!errors.name}
                />
              )}
            />
          </FormRow>
          <FormRow
            label={t('fields.phone')}
            error={phoneError(errors.phone?.message, t('errors.invalidPhone'))}
          >
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <PhoneInput
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  invalid={!!errors.phone}
                />
              )}
            />
          </FormRow>
          <Button type="submit" kind="primary" disabled={submitting}>
            {t('actions.save')}
          </Button>
        </form>
      </section>

      <section>
        <Button type="button" onClick={() => setPwOpen(true)}>
          {t('actions.changePassword')}
        </Button>
        {pwOpen ? <ChangePasswordSelfModal onClose={() => setPwOpen(false)} /> : null}
      </section>
    </div>
  );
}
